@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_shared

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.rememberCoroutineScope
import kotlinx.coroutines.launch
import org.example.project.engineslot.SLOT_BEREICH_WA
import org.example.project.engineslot.SLOT_BEREICH_WE
import org.example.project.engineslot.SlotSnapCompanyApi
import org.example.project.engineslot.searchCompanyDriverSlotBookings
import org.example.project.engineslot.searchSlotDriverSummary
import org.example.project.engineslot.loadSlotCompanyUsers
import org.example.project.engineslot.slotBookingArtCode
import org.example.project.engineslot.slotDisplayDate
import org.example.project.engineslot.slotLadenTouren
import org.example.project.engineslot.slotLiefernBelege
import org.example.project.engineslot.slotStatusLabel
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularSplitScreen
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.service.GlobalManager
import org.example.project.service.YardApi
import org.example.project.service.ZustandManager
import org.example.project.utils.All
import org.jetbrains.compose.web.css.boxSizing
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div

private const val SLOT_DRIVER_USER_ID = "slotDriverUserId"

private val SLOT_ORDER_HEADERS = listOf(
    "ID",
    "Typ",
    "Zustand",
    "Datum",
    "Zeitfenster",
    "Slot",
    "Standort",
    "Lieferschein",
    "Lieferant",
    "Empfänger",
    "Auftraggeber",
    "Unternehmer",
    "Fahrer",
    "LKW",
    "Auflieger",
    "Paletten",
    "Firma",
    "Gebucht von",
    "Prio"
)

private val DRIVER_HEADERS = listOf(
    "ID",
    "Vorname",
    "Nachname",
    "Name",
    "E-Mail",
    "Mobil",
    "Position",
    "LKW",
    "Auflieger",
    "Offen",
    "Abgeschlossen",
    "Gesamt"
)

@Composable
fun SHAREDMeineSlotAuftraege(art: String = org.example.project.service.currentSaveArt()) {
    val scope = rememberCoroutineScope()
    var bookings by remember(art) { mutableStateOf<List<All>>(emptyList()) }
    var users by remember(art) { mutableStateOf<List<All>>(emptyList()) }
    val refreshVersion = maxOf(
        LocalScreenRefreshVersion.current,
        GlobalManager.jsonDomainVersion("slot"),
        GlobalManager.jsonDomainVersion("yard"),
        GlobalManager.jsonDomainVersion("user")
    )

    LaunchedEffect(art, refreshVersion) {
        users = emptyList()
        bookings = emptyList()
    }

    val rows = bookings
        .flatMap(::slotOrderRows)
        .sortedWith(
            compareBy<All> { it.datum }
                .thenBy { it.uhrzeitVon }
                .thenBy { it.lieferscheinnummer }
                .thenBy { it.id }
        )

    TabularScreen(
        screenKey = "MeineSlotAuftraege",
        rollenKey = "MeineSlotAuftraege",
        title = "Meine Aufträge",
        headers = SLOT_ORDER_HEADERS,
        data = rows.map { it.id to slotOrderRowValues(it) },
        models = rows,
        editable = false,
        hardReadOnly = true,
        allowCreate = false,
        allowSave = false,
        allowDelete = false,
        allowImportButton = false,
        onSearchWithFilters = { filters ->
            scope.launch {
                val scopedUsers = loadSlotCompanyUsers()
                users = scopedUsers
                bookings = loadCompanyDriverSlotBookings(scopedUsers, filters)
            }
        },
        onCommit = {}
    )
}

@Composable
fun SHAREDMeineFahrerAuftraege(art: String = org.example.project.service.currentSaveArt()) {
    val scope = rememberCoroutineScope()
    var bookings by remember(art) { mutableStateOf<List<All>>(emptyList()) }
    var users by remember(art) { mutableStateOf<List<All>>(emptyList()) }
    var selectedDriverId by remember(art) { mutableStateOf("") }
    val refreshVersion = maxOf(
        LocalScreenRefreshVersion.current,
        GlobalManager.jsonDomainVersion("slot"),
        GlobalManager.jsonDomainVersion("yard"),
        GlobalManager.jsonDomainVersion("user")
    )

    LaunchedEffect(art, refreshVersion) {
        users = emptyList()
        bookings = emptyList()
    }

    val orderRows = bookings
        .flatMap(::slotOrderRows)
        .sortedWith(
            compareBy<All> { slotDriverLabel(it, users).lowercase() }
                .thenBy { slotOrderStatusSort(it) }
                .thenBy { it.auftragsart }
                .thenBy { it.datum }
                .thenBy { it.uhrzeitVon }
        )
    val driverRows = users
        .filter(::isFahrerUser)
        .sortedBy { slotUserName(it).lowercase() }
        .map { driver ->
            val driverOrders = orderRows.filter { slotOrderMatchesDriver(it, driver) }
            driver.copy(
                driverOpenCount = driver.driverOpenCount.ifBlank {
                    driverOrders.count { it.zustand < ZustandManager.ABGESCHLOSSEN }.toString()
                },
                driverClosedCount = driver.driverClosedCount.ifBlank {
                    driverOrders.count { it.zustand >= ZustandManager.ABGESCHLOSSEN || it.zustand == ZustandManager.STORNIERT }.toString()
                },
                driverTotalCount = driver.driverTotalCount.ifBlank { driverOrders.size.toString() }
            )
        }
    if (selectedDriverId.isBlank() && driverRows.isNotEmpty()) {
        selectedDriverId = driverRows.first().id
    }
    val selectedDriver = driverRows.firstOrNull { it.id == selectedDriverId }
    val selectedDriverOrders = selectedDriver
        ?.let { driver -> orderRows.filter { slotOrderMatchesDriver(it, driver) } }
        ?: emptyList()

    Div({
        style {
            height(920.px)
            minHeight(720.px)
            width(100.percent)
            boxSizing("border-box")
        }
    }) {
        TabularSplitScreen(
            initialRatio = 0.42f,
            screen1 = {
                TabularScreen(
                    screenKey = "MeineFahrerAuftraege.Fahrer",
                    rollenKey = "MeineFahrerAuftraege",
                    title = "Meine Fahrer Aufträge",
                    headers = DRIVER_HEADERS,
                    data = driverRows.map { it.id to driverRowValues(it) },
                    models = driverRows,
                    selectedRowIndices = driverRows.indexOfFirst { it.id == selectedDriverId }
                        .takeIf { it >= 0 }
                        ?.let { setOf(it) }
                        ?: emptySet(),
                    onRowClick = { rowId -> selectedDriverId = rowId },
                    editable = false,
                    hardReadOnly = true,
                    allowCreate = false,
                    allowSave = false,
                    allowDelete = false,
                    allowImportButton = false,
                    onSearchWithFilters = { filters ->
                        scope.launch {
                            users = searchSlotDriverSummary(filters)
                            selectedDriverId = users.firstOrNull()?.id.orEmpty()
                        }
                    },
                    onCommit = {}
                )
            },
            screen2 = {
                TabularScreen(
                    screenKey = "MeineFahrerAuftraege.Auftraege",
                    rollenKey = "MeineFahrerAuftraege",
                    title = "Aufträge ${selectedDriver?.let(::slotUserName)?.takeIf { it.isNotBlank() }?.let { "- $it" }.orEmpty()}",
                    headers = SLOT_ORDER_HEADERS,
                    data = selectedDriverOrders.map { it.id to slotOrderRowValues(it) },
                    models = selectedDriverOrders,
                    editable = false,
                    hardReadOnly = true,
                    allowCreate = false,
                    allowSave = false,
                    allowDelete = false,
                    allowImportButton = false,
                    onSearchWithFilters = { filters ->
                        scope.launch {
                            bookings = loadCompanyDriverSlotBookings(users, filters)
                        }
                    },
                    onCommit = {}
                )
            }
        )
    }
}

private suspend fun loadCompanyDriverSlotBookings(
    users: List<All>,
    filters: Map<String, String> = emptyMap()
): List<All> {
    val currentFirmenId = SlotSnapCompanyApi.currentFirmenId()
    if (currentFirmenId.isBlank()) return emptyList()
    val drivers = users.filter(::isFahrerUser)
    val companies = SlotSnapCompanyApi.loadSelectableCompanies()
        .filter { it.key.isNotBlank() }
        .distinctBy { it.key }

    return companies
        .flatMap { company ->
            val relevantBookings = runCatching {
                searchCompanyDriverSlotBookings(company.key, drivers.map { it.id }, filters)
            }
                .getOrDefault(emptyList())
            if (relevantBookings.isEmpty()) {
                emptyList()
            } else {
                val weLkw = runCatching { YardApi.loadWeLkwListe(company.key) }.getOrDefault(emptyList())
                val waLkw = runCatching { YardApi.loadWaLkwListe(company.key) }.getOrDefault(emptyList())
                relevantBookings
                .map { booking ->
                    val yardLkw = slotYardLkwRowsForBooking(booking, weLkw, waLkw)
                    booking.copy(
                        module = yardLkw,
                        slotKundenFirma = company.label.ifBlank { company.firma }.ifBlank { company.key }
                    )
                }
            }
        }
        .distinctBy {
            listOf(
                it.firmenId,
                it.id,
                it.datum,
                it.uhrzeitVon,
                it.slotIndex,
                it.lieferscheinnummer
            ).joinToString("|")
        }
}

private fun slotOrderRows(booking: All): List<All> {
    val art = slotBookingArtCode(booking)
    val entries = when (art) {
        SLOT_BEREICH_WA -> slotLadenTouren(booking)
        SLOT_BEREICH_WE -> slotLiefernBelege(booking)
        else -> listOf(booking)
    }
    val typ = when (art) {
        SLOT_BEREICH_WA -> "Laden"
        SLOT_BEREICH_WE -> "Liefern"
        else -> art.ifBlank { booking.typ }
    }
    return entries.mapIndexed { index, entry ->
        val driverId = entry.slotDriverUserId.ifBlank { booking.slotDriverUserId }
        val yardLkw = booking.slotYardLkwForEntry(art, entry)
        val source = yardLkw ?: entry
        entry.copy(
            id = listOf(booking.id.ifBlank { booking.lieferscheinnummer }, typ, index + 1).joinToString("|"),
            datum = booking.datum.ifBlank { source.datum },
            uhrzeitVon = booking.uhrzeitVon.ifBlank { source.uhrzeitVon.ifBlank { source.verladeuhrzeit }.ifBlank { source.uhrzeitAnmeldung } },
            uhrzeitBis = booking.uhrzeitBis.ifBlank { source.uhrzeitBis },
            standort = booking.slotStandortId.ifBlank { booking.standort },
            firmenId = source.firmenId.ifBlank { booking.firmenId },
            firma = source.firma.ifBlank { booking.firma },
            auftragsart = typ,
            prio = source.prio.ifBlank { booking.prio },
            ablaufPrio = source.ablaufPrio.takeIf { it > 0 } ?: booking.ablaufPrio,
            zustand = source.zustand.takeIf { it > 0 } ?: entry.zustand.takeIf { it > 0 } ?: booking.zustand,
            lieferscheinnummer = source.lieferscheinnummer.ifBlank { entry.lieferscheinnummer.ifBlank { booking.lieferscheinnummer } },
            lieferant = source.lieferant.ifBlank { entry.lieferant.ifBlank { booking.lieferant } },
            empfaenger = source.empfaenger.ifBlank { entry.empfaenger.ifBlank { booking.empfaenger } },
            auftraggeber = source.auftraggeber.ifBlank { entry.auftraggeber.ifBlank { booking.auftraggeber } },
            unternehmer = source.unternehmer.ifBlank { entry.unternehmer.ifBlank { booking.unternehmer } },
            fahrername = source.fahrername.ifBlank { entry.fahrername.ifBlank { booking.fahrername } },
            lkwkennzeichen = source.lkwkennzeichen.ifBlank { entry.lkwkennzeichen.ifBlank { booking.lkwkennzeichen } },
            aufliegerkennzeichen = source.aufliegerkennzeichen.ifBlank { entry.aufliegerkennzeichen.ifBlank { booking.aufliegerkennzeichen } },
            palettenanzahl = source.palettenanzahl.takeIf { it > 0 } ?: entry.palettenanzahl.takeIf { it > 0 } ?: booking.palettenanzahl,
            leerpalettenanzahl = source.leerpalettenanzahl.takeIf { it > 0 } ?: entry.leerpalettenanzahl.takeIf { it > 0 } ?: booking.leerpalettenanzahl,
            palettenWerte = source.palettenWerte.ifEmpty { entry.palettenWerte.ifEmpty { booking.palettenWerte } },
            slotDriverUserId = driverId,
            originalId = booking.id,
            slotIndex = booking.slotIndex,
            slotGebuchtVon = slotBookedBy(booking)
        )
    }
}

private fun slotYardLkwRowsForBooking(booking: All, weLkw: List<All>, waLkw: List<All>): List<All> =
    when (slotBookingArtCode(booking)) {
        SLOT_BEREICH_WE -> {
            val references = slotLiefernBelege(booking)
                .map { it.lieferscheinnummer.ifBlank { booking.lieferscheinnummer }.trim() }
                .filter { it.isNotBlank() }
                .distinct()
            weLkw.filter { lkw -> lkw.lieferscheinnummer.trim() in references }
        }

        SLOT_BEREICH_WA -> {
            val references = slotLadenTouren(booking)
                .map { it.lieferscheinnummer.ifBlank { booking.lieferscheinnummer }.trim() }
                .filter { it.isNotBlank() }
                .distinct()
            waLkw.filter { lkw -> lkw.lieferscheinnummer.trim() in references }
        }

        else -> emptyList()
    }

private fun All.slotYardLkwForEntry(art: String, entry: All): All? {
    val reference = entry.lieferscheinnummer.ifBlank { lieferscheinnummer }.trim()
    if (reference.isBlank()) return null
    val candidates = module.filter { it.lieferscheinnummer.trim() == reference }
    return when (art) {
        SLOT_BEREICH_WA -> candidates.firstOrNull {
            val lkwMatches = entry.lkwkennzeichen.isBlank() || it.lkwkennzeichen == entry.lkwkennzeichen
            val aufliegerMatches = entry.aufliegerkennzeichen.isBlank() || it.aufliegerkennzeichen == entry.aufliegerkennzeichen
            lkwMatches && aufliegerMatches
        } ?: candidates.firstOrNull()

        else -> candidates.firstOrNull()
    }
}

private fun slotOrderRowValues(entry: All): List<String> =
    listOf(
        entry.id,
        entry.auftragsart,
        slotOrderStatus(entry),
        slotDisplayDate(entry.datum),
        listOf(entry.uhrzeitVon, entry.uhrzeitBis).filter { it.isNotBlank() }.joinToString(" - "),
        entry.slotIndex,
        entry.standort,
        entry.lieferscheinnummer,
        entry.lieferant,
        entry.empfaenger,
        entry.auftraggeber,
        entry.unternehmer,
        entry.fahrername,
        entry.lkwkennzeichen,
        entry.aufliegerkennzeichen,
        entry.palettenanzahl.takeIf { it > 0 }?.toString().orEmpty(),
        entry.slotKundenFirma.ifBlank { entry.firma.ifBlank { entry.firmenId } },
        entry.slotGebuchtVon,
        entry.ablaufPrio.takeIf { it > 0 }?.toString().orEmpty().ifBlank { entry.prio }
    )

private fun driverRowValues(driver: All): List<String> =
    listOf(
        driver.id,
        driver.vorname,
        driver.nachname,
        slotUserName(driver),
        driver.email,
        driver.mobil,
        driver.position,
        driver.lkwkennzeichen,
        driver.aufliegerkennzeichen,
        driver.driverOpenCount,
        driver.driverClosedCount,
        driver.driverTotalCount
    )

private fun isFahrerUser(user: All): Boolean =
    user.position.equals("Fahrer", ignoreCase = true) ||
        user.rolle.equals("Fahrer", ignoreCase = true) ||
        user.zugangMobil

private fun slotOrderMatchesDriver(order: All, driver: All): Boolean {
    val driverId = order.slotDriverUserId
    if (driverId.isNotBlank() && driverId == driver.id) return true
    return order.fahrername.isNotBlank() && order.fahrername.equals(slotUserName(driver), ignoreCase = true)
}

private fun slotBookingHasCompanyDriver(booking: All, drivers: List<All>): Boolean =
    slotOrderRows(booking).any { order ->
        drivers.any { driver -> slotOrderMatchesDriver(order, driver) }
    }

private fun slotDriverLabel(order: All, users: List<All>): String {
    val driverId = order.slotDriverUserId
    val driver = users.firstOrNull { it.id == driverId }
    return driver?.let(::slotUserName).orEmpty().ifBlank { order.fahrername }
}

private fun slotUserName(user: All): String =
    listOf(user.vorname, user.nachname)
        .filter { it.isNotBlank() }
        .joinToString(" ")
        .ifBlank { user.name }
        .ifBlank { user.userName }
        .ifBlank { user.id }

private fun slotBookedBy(booking: All): String =
    booking.anbieterName
        .ifBlank {
            listOf(
                booking.anbieterVorname,
                booking.anbieterNachname
            ).filter { it.isNotBlank() }.joinToString(" ")
        }

private fun slotOrderStatus(entry: All): String =
    when {
        entry.zustand >= ZustandManager.ABGESCHLOSSEN -> ZustandManager.textFor(entry.zustand)
        entry.zustand == ZustandManager.STORNIERT -> ZustandManager.textFor(entry.zustand)
        entry.zustand in ZustandManager.Slot.allCodes() -> slotStatusLabel(entry.zustand, entry)
        else -> ZustandManager.textFor(entry.zustand)
    }

private fun slotOrderStatusSort(entry: All): Int =
    when {
        entry.zustand == ZustandManager.STORNIERT -> 2
        entry.zustand >= ZustandManager.ABGESCHLOSSEN -> 1
        else -> 0
    }
